import { Inputs } from "./Inputs";
import { API_KEY, SCOUT_API_BASE_URL, convertMarkdownToHTML, getClassCTemplate } from "./helpers";
import Response from "./Response";
import LanguageSelector from "./LanguageSelector";
import * as React from "react";
import { SideBySide } from "./SideBySide";
import { APIEndpointDescription } from "./api/APIEndpointCard";
import { Loader } from "./Loader";
import { Flex, Table } from "@radix-ui/themes";
import TurnstileContext, { iTurnstileContext } from "./ContextProviders/TurnstileContext";

function dataType(t: string) {

    t = t.replace("Nullable(", "")
        .replace("LowCardinality(", "")
        .replace(")", "");

    switch (t) {
        case "LowCardinality(String)":
            return "string";
        case "Int64":
        case "UInt64":
        case "UInt256":
        case "Int256":
            return "integer";
        case "DateTime":
            return "datetime";
        case "Float64":
            return "float";
        case "String":
            return "string";

        default:
            return t.toLowerCase();
    }
}

function SchemaTable({ metadata }) {
    return <Table.Root variant="surface">
        <Table.Header>
            <Table.Row>
                <Table.ColumnHeaderCell colSpan={2}>
                    Field
                </Table.ColumnHeaderCell>
            </Table.Row>
        </Table.Header>
        <Table.Body>
            {
                metadata.map((f, i) => {
                    return <Table.Row key={i}>
                        <Table.Cell>{f.name} <span className="t"> {dataType(f.type)}</span>
                        </Table.Cell>
                    </Table.Row>;
                })
            }
        </Table.Body>
    </Table.Root>;
}

interface ClassCEndpointProps {
    noPrefilledValues: any;
    codeTemplates: any[];
    endpointType: any;
    slug: any;
    schema: any;
    allClassC: any;
    userKey: any;
}

export const ClassCEndpoint: React.FC<ClassCEndpointProps> = ({ noPrefilledValues, codeTemplates = [], endpointType, slug, schema, allClassC, userKey }) => {
    const [queryResults, setQueryResults] = React.useState<any>([]);
    const [apiPath, setAPIPath] = React.useState("");
    const endpoint = getClassCTemplate(allClassC, slug, endpointType);
    const [descriptionHTML] = React.useState(convertMarkdownToHTML(endpoint ? endpoint.description : ""));

    const [loading, setLoading] = React.useState(false);

    const replacePathValues = (path: any, params: { [x: string]: any }) => {
        if (!path || !params) {
            return false;
        }
        const paramNames = Object.keys(params);
        let constructedPath = path;
        paramNames.forEach((param) => {
            constructedPath = constructedPath.replaceAll(`:${param}`, params[param].value);
        });
        return constructedPath;
    };

    const sanitizeValue = (rawValue: any) => {
        let value = rawValue;
        if (value === true) {
            value = "true";
        } else if (value === false) {
            value = "false";
        }
        return value;
    };

    const addQueryValues = (constructedPath: string, queryParams: any) => {
        let path = constructedPath + "?";
        if (!path || !queryParams) {
            return false;
        }
        const paramNames = Object.keys(queryParams);
        paramNames.forEach((param) => {
            const value = sanitizeValue(queryParams[param].value);
            if (queryParams[param].queryParam) {
                path = path + `&${param}=${value}`;
            }
        });

        return path;
    };

    const constructQuery = (params: any) => {
        const constructedPath = replacePathValues(endpoint.path.replaceAll(":app_name", slug), params);
        const fullConstructedURL = addQueryValues(constructedPath, params);
        if (!fullConstructedURL) {
            return false;
        }
        setAPIPath(fullConstructedURL);
        return fullConstructedURL;
    };

    const context = React.useContext<iTurnstileContext>(TurnstileContext);

    const queryAPI = async (constructedQuery: string) => {
        if (!context.needsVerification) {
            try {
                setLoading(true);
                const result = await fetch(SCOUT_API_BASE_URL + constructedQuery, {
                    headers: {
                        "X-Requested-With": "com.covalenthq.www.docs.api",
                        "X-CF-Turnstile-Token": context.validID
                    }
                });
                const response = await result.json();
                setLoading(false);
    
                return response;
            } catch (error) {
                setLoading(false);
                return error;
            }
        }
    };

    const runQuery = async (params: { [x: string]: any }) => {
        const constructedQuery = constructQuery(params);

        if (!constructedQuery) {
            return false;
        } else {
            const response = await queryAPI(constructedQuery + `&key=${API_KEY}`);
            setQueryResults(response);
        }

    };

    const endpointPath = `${SCOUT_API_BASE_URL}${apiPath ? apiPath : endpoint && endpoint.path.replaceAll(":app_name", slug)}`;

    const handleOpenInNew = () => {
        if(userKey){
            window.open(`${endpointPath}&key=${userKey.id}`, "_blank");
        }else{
            const data = JSON.stringify(queryResults.data);
            const blob = new Blob([data], { type: "application/json" });
            const url = URL.createObjectURL(blob);
            window.open(url);
        }

    };


    return !endpoint ?
        <div>Not supported.</div>
        : <div>
            <Flex direction="column" gap="6" mt="4" mb="4">
                <h3>{endpoint.name}</h3>
                <APIEndpointDescription
                    beta={false}
                    path={endpoint.path.replaceAll(":app_name", slug)}
                    description={descriptionHTML}
                    usecase=""
                    pricing="1.00 Credits"
                />
                <SideBySide>
                    <div>
                        <div className="parameters">
                            <Inputs
                                load={loading ? true : undefined}
                                classC={true}
                                noPrefilledValues={noPrefilledValues}
                                runQuery={runQuery}
                                chainName={false} params={endpoint.params ? endpoint.params : undefined} templateParams={[
                                    {
                                        "name": "address",
                                        "value": endpoint.samplewallet
                                    }
                                ]}
                                constructQuery={constructQuery}
                            />
                        </div>
                    </div>
                    <div>
                        <LanguageSelector userKey={userKey} codeTemplates={codeTemplates} url={endpointPath} params={[]} />
                        {!loading ? <Response handleOpenInNew={handleOpenInNew} response={queryResults} /> : null}
                        <Loader loading={loading} />
                    </div>
                </SideBySide>
                <h3>Schema</h3>
                <SchemaTable
                    metadata={schema.data.metadata}
                />
            </Flex>

            <style jsx>
                {`
                    .lang-selector {
                        padding-bottom: 1.5rem;
                    }
                    .title {
                        padding-top: 1rem;
                        padding-bottom: 1rem;
                    }
                `}
            </style>
        </div>;
};
